import { useCallback, useMemo } from 'react';
import {
  HapticNotificationFeedbackStyle,
  triggerHapticNotificationFeedback,
} from '@sg/garnish';

import type {
  AddLineItemToCartInput,
  DeliveryOrderDetailInput,
  IngredientModificationInput,
  IngredientSubstitutionModificationInput,
  MixedDressingDetails,
  PartialProduct,
} from '@order/graphql';
import { useAddLineItemToCartMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { useDeliveryOrderInFlight } from '../../useDeliveryOrderInFlight';
import { useNetworkErrorNotice } from '../../useNetworkErrorNotice';
import { useCart } from '../useCart';
import {
  extractIngredientId,
  extractSubstitution,
  getLineItemCustomName,
} from '../useCart.utils';
import { useCartMutationErrorNotice } from '../useLineItemErrorResponse';

// ────────────────────────────────────────────────────────────────────────────────

export const useAddLineItemToCart = (props?: UseAddItemToCart) => {
  const { onSuccess } = props ?? {};
  const { executeCartMutation } = useCart();
  const { t } = useLocalizationContext();

  const [addLineItemResponse, addLineItem] = useAddLineItemToCartMutation();
  const { data, fetching } = addLineItemResponse;

  useCartMutationErrorNotice(data?.addLineItemToCart);
  useNetworkErrorNotice(addLineItemResponse);

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── TELEMETRY ───────────────────────────────────────────────────

  const { track } = useTelemetry();

  const onAddToBagFailedTelemetry = useCallback(
    (userError?: string, systemError?: string) => {
      track('add_to_bag.failed', {
        userError: userError ?? t('general.error'),
        systemError,
      });
    },
    [t, track],
  );

  // ─── HELPERS ─────────────────────────────────────────────────────

  const addLineItemToCart = useCallback(
    async (inputProps: AddItemToCartInputProps) => {
      // Block adding delivery product when there is an in-flight delivery order.
      if (inputProps.deliveryDetails?.addressId && inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const {
        product,
        quantity = 1,
        modifications,
        deliveryDetails,
        customName = null,
      } = inputProps;

      const { id: productId = '', name: productName = '' } = product;

      const {
        additions = [],
        removals = [],
        substitutions = [],
        mixedDressingDetails = [],
      } = modifications ?? {};

      const deliveryOrderDetails = {
        addressId: deliveryDetails?.addressId ?? '',
        vendor: deliveryDetails?.vendor ?? '',
        vendorRestaurantId: deliveryDetails?.vendorRestaurantId ?? '',
        deliveryFee: deliveryDetails?.deliveryFee ?? 0,
        tip: deliveryDetails?.tip ?? 0,
      };

      const input: AddLineItemToCartInput = {
        productId,
        customName: getLineItemCustomName({ productName, customName }),
        quantity,
        mixedDressingDetails,
        additions: additions.map(extractIngredientId),
        removals: removals.map(extractIngredientId),
        substitutions: substitutions.map(extractSubstitution),
        ...(deliveryOrderDetails?.addressId ? { deliveryOrderDetails } : {}),
      };

      const executeMutation = async () => addLineItem({ input });
      const response = await executeCartMutation(
        executeMutation,
        'add-line-item',
      );
      const result = response.data?.addLineItemToCart;
      const possibleError = result as { readonly message?: string };
      const isSuccessfullyAdded = Boolean(
        result?.__typename === 'AddLineItemToCartSuccess',
      );

      if (!isSuccessfullyAdded) {
        onAddToBagFailedTelemetry(
          possibleError?.message,
          getUrqlError(response.error),
        );

        return;
      }

      void triggerHapticNotificationFeedback(
        HapticNotificationFeedbackStyle.Success,
      );
      onSuccess?.();
    },
    [
      inFlightDeliveryOrderId,
      executeCartMutation,
      onSuccess,
      navigateToDeliveryOrderInFlightScreen,
      addLineItem,
      onAddToBagFailedTelemetry,
    ],
  );

  // ─────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({ addLineItemToCart, fetching }),
    [addLineItemToCart, fetching],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseAddItemToCart = {
  readonly onSuccess?: () => void;
};

type AddItemToCartInputProps = {
  readonly product: PartialProduct;
  readonly customName?: string | null;
  readonly quantity?: number;
  readonly deliveryDetails?: DeliveryOrderDetailInput;
  readonly modifications?: {
    readonly additions: readonly IngredientModificationInput[];
    readonly removals: readonly IngredientModificationInput[];
    readonly substitutions: readonly IngredientSubstitutionModificationInput[];
    readonly mixedDressingDetails: readonly MixedDressingDetails[];
  };
};
